import { useState, useEffect, createContext, useContext } from 'react'
import { api } from '../services/api'

const AuthContext = createContext()
const IMPERSONATION_OWNER_TOKEN_KEY = 'impersonation_owner_token'
const IMPERSONATION_META_KEY = 'impersonation_meta'

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const localToken = localStorage.getItem('token')
    const sessionToken = sessionStorage.getItem('token')
    const token = localToken || sessionToken
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`
      fetchUser()
    } else {
      setIsLoading(false)
    }
  }, [])

  const setSessionToken = (token, rememberSession = true) => {
    if (rememberSession) {
      localStorage.setItem('token', token)
      sessionStorage.removeItem('token')
    } else {
      sessionStorage.setItem('token', token)
      localStorage.removeItem('token')
    }
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`
  }

  const clearImpersonationState = () => {
    localStorage.removeItem(IMPERSONATION_OWNER_TOKEN_KEY)
    sessionStorage.removeItem(IMPERSONATION_OWNER_TOKEN_KEY)
    localStorage.removeItem(IMPERSONATION_META_KEY)
    sessionStorage.removeItem(IMPERSONATION_META_KEY)
  }

  const fetchUser = async () => {
    try {
      const response = await api.get('/auth/profile')
      setUser(response.data.data)
    } catch (error) {
      console.error('Error fetching user:', error)
      localStorage.removeItem('token')
      sessionStorage.removeItem('token')
      delete api.defaults.headers.common['Authorization']
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (credentials, options = {}) => {
    const { rememberSession = true } = options

    try {
      const response = await api.post('/auth/login', credentials)
      const { user, token } = response.data.data

      clearImpersonationState()
      setSessionToken(token, rememberSession)
      setUser(user)
      
      return { success: true, user }
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Error de autenticación' 
      }
    }
  }

  const logout = () => {
    localStorage.removeItem('token')
    sessionStorage.removeItem('token')
    clearImpersonationState()
    delete api.defaults.headers.common['Authorization']
    setUser(null)
  }

  const updateUser = (updatedUser) => {
    setUser(updatedUser)
  }

  const startImpersonation = async ({ token, tenant, targetUser }) => {
    const currentToken = localStorage.getItem('token') || sessionStorage.getItem('token')
    if (!currentToken) throw new Error('No se encontró sesión owner activa')

    const useLocalStorage = Boolean(localStorage.getItem('token'))
    const targetStorage = useLocalStorage ? localStorage : sessionStorage
    targetStorage.setItem(IMPERSONATION_OWNER_TOKEN_KEY, currentToken)
    targetStorage.setItem(
      IMPERSONATION_META_KEY,
      JSON.stringify({
        tenantId: tenant?.id || null,
        tenantName: tenant?.name || '',
        tenantSlug: tenant?.slug || '',
        targetUserId: targetUser?.id || null,
        targetUserEmail: targetUser?.email || '',
        startedAt: new Date().toISOString()
      })
    )
    setSessionToken(token, useLocalStorage)
    await fetchUser()
  }

  const endImpersonation = async () => {
    const ownerToken =
      localStorage.getItem(IMPERSONATION_OWNER_TOKEN_KEY) ||
      sessionStorage.getItem(IMPERSONATION_OWNER_TOKEN_KEY)
    if (!ownerToken) {
      clearImpersonationState()
      throw new Error('No hay sesión owner para restaurar')
    }

    try {
      await api.post('/auth/impersonation/end', { reason: 'owner_restore' })
    } catch (error) {
      console.warn('No se pudo registrar fin de impersonación:', error?.message || error)
    }

    const useLocalStorage = Boolean(localStorage.getItem(IMPERSONATION_OWNER_TOKEN_KEY))
    clearImpersonationState()
    setSessionToken(ownerToken, useLocalStorage)
    await fetchUser()
  }

  const impersonationMetaRaw =
    localStorage.getItem(IMPERSONATION_META_KEY) || sessionStorage.getItem(IMPERSONATION_META_KEY)
  let impersonationMeta = null
  try {
    impersonationMeta = impersonationMetaRaw ? JSON.parse(impersonationMetaRaw) : null
  } catch {
    impersonationMeta = null
  }
  const isImpersonating = Boolean(user?.impersonation?.isImpersonating && impersonationMeta)

  const value = {
    user,
    isLoading,
    login,
    logout,
    updateUser,
    isImpersonating,
    impersonationMeta,
    startImpersonation,
    endImpersonation
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
